import { noop } from 'lodash';
import styled from 'styled-components';

import {
   AppointmentReceptionService,
   AppointmentReceptionServiceQuery,
   AppointmentSlotSchedulePatient,
} from '@dreryk/egabinet-appointment-frontend-api';
import { AppointmentId } from '@dreryk/egabinet-appointment-published-language-api';
import { VisitSettlement, VisitSettlementQuery } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { PurchaseId } from '@dreryk/egabinet-financial-settlement-published-language-api';
import { CurrentHealthcareProviderInvoiceIntegrationEnabledQuery } from '@dreryk/egabinet-invoice-frontend-api';

import { useCqrsFind, useCqrsGet, useCqrsSearch } from '@dreryk/command-query-react-common';
import { convertDateToLocalDate } from '@dreryk/react-common';
import {
   ContextDialog,
   FluidTab,
   FluidTabs,
   LoaderSpinner,
   Notification,
   NotificationType,
   Text,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { visitSettlementTranslationKeys } from '../../../translations';

import { VisitSettlementCalendarTabContent } from './VisitSettlementCalendarTabContent';
import { VisitSettlementCalendarTabContextDialogButton } from './VisitSettlementCalendarTabContextDialogButton';
import { VisitSettlementCalendarTabContextDialogInvoiceCreateButton } from './VisitSettlementCalendarTabContextDialogInvoiceCreateButton';
import { isInvoiceCreateButtonAvailable } from './useVisitSettlementCalendarFluidTab.utils';

const t = visitSettlementTranslationKeys.extensions.calendar.tab;

const StyledPlaceholderText = styled(Text)`
   color: ${(props) => props.theme.placeholder};
`;

StyledPlaceholderText.displayName = 'StyledPlaceholderText';

type VisitSettlementLoaderProps = {
   readonly appointmentId: number;
   readonly children: (renderProps: {
      invoiceProviderIntegrationEnabled: boolean;
      visitSettlement: VisitSettlement | null;
      receptionServices: AppointmentReceptionService[];
   }) => JSX.Element;
};

const VisitSettlementLoader = (props: VisitSettlementLoaderProps): JSX.Element | null => {
   const invoiceCreateAvailable = protectFeature('creatingInvoices');

   const {
      data: invoiceProviderIntegrationEnabled,
      loading: invoiceProviderIntegrationEnabledLoading,
      error: invoiceProviderIntegrationEnabledError,
   } = useCqrsGet<boolean>(() => new CurrentHealthcareProviderInvoiceIntegrationEnabledQuery(), {
      enabled: invoiceCreateAvailable,
   });

   const {
      data: visitSettlement,
      loading: visitSettlementLoading,
      error: visitSettlementError,
   } = useCqrsFind<VisitSettlement>(
      () => {
         const query = new VisitSettlementQuery();
         query.purchaseIds = [PurchaseId.forAppointment(AppointmentId.of(props.appointmentId))];
         return query;
      },
      {
         onUnexpectedError: noop,
      }
   );

   const {
      data: receptionServices,
      loading: receptionServicesLoading,
      error: receptionServicesError,
   } = useCqrsSearch<AppointmentReceptionService>(() => {
      const query = new AppointmentReceptionServiceQuery();
      query.appointmentId = AppointmentId.of(props.appointmentId);
      return query;
   });

   if (visitSettlementLoading || receptionServicesLoading || invoiceProviderIntegrationEnabledLoading) {
      return <LoaderSpinner />;
   }

   if (visitSettlementError || receptionServicesError || invoiceProviderIntegrationEnabledError) {
      return <Notification testId={'error'} type={NotificationType.ERROR} label={t.error} />;
   }

   return props.children({
      invoiceProviderIntegrationEnabled: invoiceProviderIntegrationEnabled,
      visitSettlement: visitSettlement,
      receptionServices: receptionServices,
   });
};

const useReadOnlyVisitSettlement = (appointmentId: number): VisitSettlement | null => {
   const privateMedicalServicesReadOnlyAvailable = protectFeature('privateMedicalServicesReadOnly');

   const { data } = useCqrsFind<VisitSettlement>(
      () => {
         const query = new VisitSettlementQuery();
         query.purchaseIds = [PurchaseId.forAppointment(AppointmentId.of(appointmentId))];
         return query;
      },
      {
         enabled: privateMedicalServicesReadOnlyAvailable,
         onUnexpectedError: noop,
      }
   );

   return data ?? null;
};

type UseVisitSettlementCalendarFluidTabOptions = {
   appointmentId: number;
   practitionerId: number;
   ownedByCurrentUser: boolean;
   onClose: VoidFunction;
   beginDate: Date;
   endDate: Date;
   patient: AppointmentSlotSchedulePatient;
};

export const useVisitSettlementCalendarFluidTab = (
   options: UseVisitSettlementCalendarFluidTabOptions
): FluidTab | null => {
   const invoiceCreateAvailable = protectFeature('creatingInvoices');
   const settlingMedicalServicesPerformedByOneselfAvailable = protectFeature(
      'settlingMedicalServicesPerformedByOneself'
   );
   const settlingMedicalServicesPerformedByAnyPractitionerAvailable = protectFeature(
      'settlingMedicalServicesPerformedByAnyPractitioner'
   );
   const privateMedicalServicesReadOnlyAvailable = protectFeature('privateMedicalServicesReadOnly');
   const readOnlyVisitSettlement = useReadOnlyVisitSettlement(options.appointmentId);

   const settlementActionsAvailable =
      settlingMedicalServicesPerformedByOneselfAvailable || settlingMedicalServicesPerformedByAnyPractitionerAvailable;

   if (!settlementActionsAvailable && !(privateMedicalServicesReadOnlyAvailable && readOnlyVisitSettlement)) {
      return null;
   }

   return (
      <FluidTabs.Tab id={'visitSettlement'} testId={'visitSettlement'} label={t.label}>
         <VisitSettlementLoader appointmentId={options.appointmentId}>
            {({ visitSettlement, receptionServices, invoiceProviderIntegrationEnabled }) => (
               <>
                  <VisitSettlementCalendarTabContent
                     receptionServices={receptionServices}
                     visitSettlement={visitSettlement}
                  />
                  <ContextDialog.CloseButton onClick={options.onClose} />
                  {!privateMedicalServicesReadOnlyAvailable &&
                  isInvoiceCreateButtonAvailable(
                     invoiceCreateAvailable,
                     invoiceProviderIntegrationEnabled,
                     visitSettlement
                  ) ? (
                     <VisitSettlementCalendarTabContextDialogInvoiceCreateButton
                        disabled={false}
                        patientId={options.patient.id}
                        visitDate={convertDateToLocalDate(options.beginDate)}
                        appointmentId={options.appointmentId}
                        visitSettlementId={visitSettlement!.id}
                     />
                  ) : null}
                  {privateMedicalServicesReadOnlyAvailable ? null : (
                     <VisitSettlementCalendarTabContextDialogButton
                        invoiceProviderIntegrationEnabled={invoiceProviderIntegrationEnabled}
                        appointmentId={options.appointmentId}
                        ownedByCurrentUser={options.ownedByCurrentUser}
                        practitionerId={options.practitionerId}
                        visitSettlement={visitSettlement}
                        visitDetails={{
                           beginDate: options.beginDate,
                           endDate: options.endDate,
                           patient: {
                              firstName: options.patient.firstName?.asString() ?? null,
                              lastName: options.patient.lastName?.asString() ?? null,
                              id: options.patient.id,
                           },
                        }}
                     />
                  )}
               </>
            )}
         </VisitSettlementLoader>
      </FluidTabs.Tab>
   );
};
